import { useMutation, useQuery } from "@tanstack/react-query";

import {
  INDIAN_ISD_CODE,
  LEARN_VERTICAL_ID,
  SCA_VERTICAL_ID,
} from "constants/constants";
import { DAY, QUERY_RETRY_ATTEMPTS } from "constants/queryCacheTimes";
import Cookies from "js-cookie";

const transformKHUserToUserFormat = (khUser) => {
  console.log("Transforming KH User:", khUser);
  return [
    {
      firstName: khUser.first_name,
      lastName: khUser.last_name,
      phone: khUser.phone,
      uamId: khUser.uam_id,
      verticalId: SCA_VERTICAL_ID,
    },
  ];
};

const fetchKHUser = async (phoneNumber) => {
  const url = process.env.REACT_APP_GET_VERIFY_USER;

  const authToken = Cookies.get("jweToken");

  const payload = { isd_code: INDIAN_ISD_CODE, phone: phoneNumber };

  const headers = {
    accept: "application/json",
    "product-id": "900",
    "tenant-id": "7",
    platform: "web",
    Authorization: `Bearer ${authToken}`,
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorBody = await response.json();
    const errorMessage = errorBody?.detail?.message || "Something went wrong.";
    throw new Error(errorMessage);
  }

  const khUserData = await response.json();

  if (!khUserData?.users || khUserData.users.length === 0) {
    throw new Error("No user found for the provided phone number or UAM ID.");
  }

  const transformedKHUsers = transformKHUserToUserFormat(khUserData.users[0]);
  return transformedKHUsers;
};

const transformLearnUsersWithValidation = (globalUsers) => {
  if (!globalUsers || !Array.isArray(globalUsers.accounts)) return [];

  return globalUsers.accounts.flatMap((account) => {
    if (!Array.isArray(account.profiles)) return [];

    const matchingProfiles = account.profiles.filter(
      (profile) => Number(profile.vertical_id) === Number(LEARN_VERTICAL_ID)
    );

    if (matchingProfiles.length > 1) {
      throw new Error(
        "Seems like there is an issue with this customer's phone number/UAM ID. Please escalate this customer to the support team for resolution."
      );
    }

    return matchingProfiles.map((profile) => ({
      firstName: account.first_name,
      lastName: account.last_name,
      phone: account.phone,
      verticalId: profile.vertical_id,
      uamId: profile.uam_id,
    }));
  });
};

const fetchLearnUser = async (phoneNumber) => {
  const authToken = Cookies.get("jweToken");
  const url = process.env.REACT_APP_ONE_APP_SEARCH_USER_URL;

  const payload = { isd_code: INDIAN_ISD_CODE, phone: phoneNumber };

  const headers = {
    accept: "application/json",
    "product-id": "100",
    "tenant-id": "99999",
    platform: "web",
    Authorization: `Bearer ${authToken}`,
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });

  const globalUsers = await response.json();

  if (response.status !== 200) {
    throw new Error("An error occurred while requesting Customer Information.");
  }

  const filtered_user_data = transformLearnUsersWithValidation(globalUsers);

  if (filtered_user_data.length === 0) {
    throw new Error("No user found for the provided phone number");
  }

  return filtered_user_data;
};

const fetchCustomerInfo = async (payload) => {
  if (Number(payload.selectedBusinessVertical) === SCA_VERTICAL_ID) {
    return await fetchKHUser(payload.phoneNumber);
  }
  if (Number(payload.selectedBusinessVertical) === LEARN_VERTICAL_ID) {
    return await fetchLearnUser(payload.phoneNumber);
  }
};

export const useFetchCustomerInfo = () => {
  return useMutation({ mutationFn: fetchCustomerInfo });
};

const fetchCustomerOrderDetails = async (customerId) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/fetch-customer-orders-details`
  );

  const headers = {
    accept: "application/json",
    "secret-key": `${process.env.REACT_APP_ONE_OMS_SECRET_KEY}`,
    "Content-Type": "application/json",
  };

  const payload = { uam_id: customerId };

  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });

  const data = await response.json();

  if (data.status && data.status === "success") {
    return data.data.order_details;
  } else {
    throw new Error(
      data.message || "An error occurred while processing the request."
    );
  }
};

export const useFetchCustomerOrderDetails = (customerId) => {
  return useQuery({
    queryKey: ["customer_order_details", customerId],
    queryFn: () => fetchCustomerOrderDetails(customerId),
    enabled: !!customerId,
  });
};

const fetchOrderDetails = async (orderId) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/get-order-info/${orderId}`
  );

  const headers = {
    accept: "application/json",
    "secret-key": `${process.env.REACT_APP_ONE_OMS_SECRET_KEY}`,
    "Content-Type": "application/json",
  };

  const response = await fetch(url, { headers });

  const data = await response.json();

  if (data.status && data.status === "success") {
    return data.data.order_details;
  } else {
    throw new Error(
      data.message || "An error occurred while processing the request."
    );
  }
};

export const useFetchOrderDetails = (orderId) => {
  return useQuery({
    queryKey: ["order_details", orderId],
    queryFn: () => fetchOrderDetails(orderId),
    enabled: !!orderId,
  });
};

const markChequeReceived = async (payload) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/update-cheque-status`
  );
  const headers = {
    accept: "application/json",
    "secret-key": `${process.env.REACT_APP_ONE_OMS_SECRET_KEY}`,
    "Content-Type": "application/json",
  };
  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data?.message ||
        "Oops! Something went wrong. Please refresh the page and try again."
    );
  }
};

export const useMarkChequeReceived = () => {
  return useMutation({ mutationFn: markChequeReceived });
};

const fetchChequeAcknowledgement = async (paymentId) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/get-cheque-acknowledgement/${paymentId}`
  );

  const headers = {
    accept: "application/json",
    "secret-key": process.env.REACT_APP_ONE_OMS_SECRET_KEY,
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.message || `Failed to fetch cheque acknowledgement.`
    );
  }

  const data = await response.json();

  if (data.status === "success") {
    return { signedUrl: data.data.signed_url };
  } else {
    throw new Error(
      data.message || "An error occurred while fetching cheque acknowledgement."
    );
  }
};

export const useFetchChequeAcknowledgement = (payment_id) => {
  const query = useQuery({
    queryKey: ["ChequeAcknowledgement", payment_id],
    queryFn: () => fetchChequeAcknowledgement(payment_id),
    enabled: !!payment_id,
  });
  return {
    ...query,
    fetchChequeAcknowledgement,
  };
};

const banks = async () => {
  const url = new URL(`${process.env.REACT_APP_ONE_OMS_URL}/get-banks-list`);
  const headers = {
    accept: "application/json",
    "secret-key": `${process.env.REACT_APP_ONE_OMS_SECRET_KEY}`,
    "Content-Type": "application/json",
  };
  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });
  const data = await response.json();
  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data.message || "An error occurred while processing the request."
    );
  }
};
export const useFetchAllBanks = () => {
  return useQuery({
    queryKey: ["Banks"],
    queryFn: () => banks(),
    staleTime: DAY,
    cacheTime: DAY,
    retry: QUERY_RETRY_ATTEMPTS,
  });
};

const fetchOrderDetailsByAdmissionNumber = async (applicationNumber) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/get-order-by-application-number/${applicationNumber}`
  );
  const headers = {
    accept: "application/json",
    "secret-key": process.env.REACT_APP_ONE_OMS_SECRET_KEY,
    "Content-Type": "application/json",
  };
  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });
  const data = await response.json();
  if (data.status && data.status === "success") {
    return data.data.order_id;
  } else {
    throw new Error(
      data?.message ||
        "Oops! Something went wrong. Please refresh the page and try again."
    );
  }
};

export const useFetchOrderDetailsByAdmissionNumber = () => {
  return useMutation({ mutationFn: fetchOrderDetailsByAdmissionNumber });
};

const fetchOrderInvoice = async (order_id) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/get-tax-invoice/${order_id}`
  );

  const headers = {
    accept: "application/json",
    "secret-key": process.env.REACT_APP_ONE_OMS_SECRET_KEY,
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || `Failed to fetch tax invoice.`);
  }

  const data = await response.json();

  if (data.status === "success") {
    return { signedUrl: data.data.signed_url };
  } else {
    throw new Error(
      data.message || "An error occurred while fetching tax invoice."
    );
  }
};

export const useFetchOrderInvoice = (order_id) => {
  const query = useQuery({
    queryKey: ["OrderInvoice", order_id],
    queryFn: () => fetchOrderInvoice(order_id),
    enabled: !!order_id,
  });
  return {
    ...query,
    fetchOrderInvoice,
  };
};

const fetchOrderSummaryPDF = async (orderId) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/generate-or-get-order-summary/${orderId}`
  );

  const headers = {
    accept: "application/json",
    "secret-key": process.env.REACT_APP_ONE_OMS_SECRET_KEY,
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });

  const data = await response.json();

  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data.message || "An error occurred while processing the request."
    );
  }
};

export const useFetchOrderSummaryPDF = (orderId) => {
  return useQuery({
    queryKey: ["OrderSummary", orderId],
    queryFn: () => fetchOrderSummaryPDF(orderId),
    enabled: false,
    retry: false,
  });
};

const fetchPaymentReceipt = async (payment_id) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/get-payment-receipt/${payment_id}`
  );

  const headers = {
    accept: "application/json",
    "secret-key": process.env.REACT_APP_ONE_OMS_SECRET_KEY,
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || `Failed to fetch Payment Receipt.`);
  }

  const data = await response.json();

  if (data.status === "success") {
    return { signedUrl: data.data.signed_url };
  } else {
    throw new Error(
      data.message || "An error occurred while fetching Payment Receipt."
    );
  }
};

export const useFetchPaymentReceipt = (payment_id) => {
  const query = useQuery({
    queryKey: ["PaymentReceipt", payment_id],
    queryFn: () => fetchPaymentReceipt(payment_id),
    enabled: !!payment_id,
  });
  return {
    ...query,
    fetchPaymentReceipt,
  };
};
